import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ActionNodeData } from '@app/services/aiFlow.types';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';

type ActionFormProps = {
    readonly id: string;
    readonly data: ActionNodeData;
    readonly number: string | null;
    readonly onSubmit: (data: ActionNodeData) => void;
    readonly onCancel: () => void;
};

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    description: z.string().trim().min(1, 'Description is required'),
    isSubsection: z.boolean(),
    isFirstSubsection: z.boolean(),
    visConditionSlots: z.coerce.number().min(0).max(3),
});

function ActionForm({ id, data, number, onSubmit, onCancel }: ActionFormProps) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: data,
    });

    useEffect(() => {
        reset(data);
    }, [data, reset]);

    return (
        <>
            <div className="order-tag">
                <span>{number ?? '--'}</span>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} key={id}>
                <div>
                    <label htmlFor="name-input">
                        Name
                        <textarea {...register('name')} id="name-input" autoFocus />
                    </label>
                </div>
                <div>
                    <label htmlFor="description-input">
                        Description
                        <textarea {...register('description')} id="description-input" />
                    </label>
                </div>
                <div className="grid">
                    <label htmlFor="isSubsection-input" className="inline-label">
                        Subsection
                        <input
                            {...register('isSubsection')}
                            id="isSubsection-input"
                            type="checkbox"
                        />
                    </label>
                    <label htmlFor="isFirstSubsection-input" className="inline-label">
                        New Section
                        <input
                            {...register('isFirstSubsection')}
                            id="isFirstSubsection-input"
                            type="checkbox"
                        />
                    </label>
                    <label htmlFor="visConditionSlots-input" className="inline-label">
                        <span>Condition Slots</span>
                        <input
                            {...register('visConditionSlots')}
                            id="visConditionSlots-input"
                            type="number"
                            max={3}
                            min={0}
                        />
                    </label>
                </div>
                <StyledButtonGroup className="margin-top-10">
                    <StyledButton htmlType="submit" disabled={!isValid || !isDirty}>
                        Save
                    </StyledButton>
                    <StyledButton htmlType="button" onClick={onCancel}>
                        Cancel
                    </StyledButton>
                </StyledButtonGroup>
            </form>
        </>
    );
}

export default ActionForm;
